import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { RentalPeriodModel } from 'src/app/models/rental/rental-period.model';
import { BranchService } from 'src/app/services/management/branch.service';
import { RentalPeriodService } from 'src/app/services/rental/rental-period.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-rental-period',
  templateUrl: './register-rental-period.component.html',
  styleUrls: ['./register-rental-period.component.scss'],
  standalone: false
})
export class RegisterRentalPeriodComponent implements OnInit {

  formRentalPeriod: FormGroup = new FormGroup({});
  rentalPeriodContract: RentalPeriodModel = new RentalPeriodModel();
  listBranches: any[] = [];
  permissionUpdatable: boolean = false;
  subscription: Subscription;

  constructor(private branchService: BranchService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService,
    private rentalPeriodService: RentalPeriodService,
    @Optional() private ref: DynamicDialogRef) {
    this.validateForm(new RentalPeriodModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.rentalPeriodContract = inputs.rentalPeriodContract;
      this.permissionUpdatable = inputs.permissionUpdatable;
    });
    await this.getAllBranchesForSelect();
    this.createNewRegister();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async changeBranch(branchId: number) {
    this.rentalPeriodContract = new RentalPeriodModel();
    this.rentalPeriodContract.branchId = branchId;
    this.validateForm(this.rentalPeriodContract);
    this.setYear();
  }

  close() {
    this.ref.close();
    this.rentalPeriodContract = new RentalPeriodModel();
    this.validateForm(this.rentalPeriodContract);
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear períodos de alquiler.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.validateForm(this.rentalPeriodContract);
    this.setYear();
  }

  async getAllBranchesForSelect() {
    try {
      this.listBranches = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
    } catch (error) {
      console.error('Error al obtener las sucursales.', error);
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  setYear() {
    const currentYear = new Date().getFullYear();
    this.formRentalPeriod.get('year')?.setValue(currentYear);
  }

  async send() {
    this.validateFormControls();
    if (this.formRentalPeriod.valid) {
      if (!this.permissionUpdatable) {
        this.toastHandlerService.showToast('error', `El usuario no tiene permisos para crear períodos de alquiler.
          Comuníquese con el administrador del sistema.`);
        return;
      }
      const toastId = 'rentalPeriod';
      const modelInventoryPeriod = this.formRentalPeriod.getRawValue() as RentalPeriodModel;
      this.toastHandlerService.showToast('info', 'Se crearán los saldos iniciales; este proceso puede tardar un poco.',
        'Información', toastId, 0);
      try {
        await firstValueFrom(this.rentalPeriodService.createRentalPeriod(modelInventoryPeriod));
        this.toastHandlerService.clearToastById(toastId);
        this.onReloadEmit();
        this.toastHandlerService.showToast('success', `El período de alquiler fue creado exitosamente.`);
      } catch (error) {
        this.toastHandlerService.clearToastById(toastId);
        console.error('Error al crear el período de alquiler.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sede es obligatorio.',
      },
      year: {
        required: 'El campo Año es obligatorio.',
        minlength: 'El campo Año debe tener al menos 4 dígitos.',
      },
      period: {
        required: 'El campo Período es obligatorio.',
        min: 'El período mínimo permitido es 1.',
        max: 'El período máximo permitido es 12.',
      },
    };

    Object.keys(controlMessages).forEach((controlName) => {
      const formControl = this.formRentalPeriod.get(controlName);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[controlName];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(rentalPeriodModel: RentalPeriodModel) {
    this.formRentalPeriod = this.formBuilder.group({
      branchId: [rentalPeriodModel.branchId, [Validators.required]],
      year: [rentalPeriodModel.year, [Validators.required, Validators.minLength(4)]],
      period: [rentalPeriodModel.period, [Validators.required, Validators.min(1), Validators.max(12)]]
    });
  }

  validateFormControls() {
    Object.keys(this.formRentalPeriod.controls).forEach((key) => {
      const control = this.formRentalPeriod.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
